import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { History } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import AppFormatResourceStatusLabel from "src/components/FormatStatus";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { ProductTransferApi } from "../../../../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../../../../services/Api/api";
import { ProductMobileActionsContext } from "../../../../../../../../components/Tabs/ProductEdit/ProductEdit";

interface ListProps {
	resource?: Record<string, any>;
	productId: number | string;
}

const listName = "PRODUCT_ORDER";
const resourceType = "PRODUCT_ORDER";

const buildConfig = (
	t: TFunction<"translation", undefined>,
	data: Record<string, any>,
	segmentListService: SegmentListService,
	history: History,
	organization: OrganizationState,
	productId: number | string
) => {
	let config = {
		fields: [
			{
				id: "date",
				name: t("lib:common.word.date"),
				type: "date",
				render: (item: ProductTransferApi) => {
					return (
						<Link to={`/${organization.id}/documents/transfers/${item.order_id}`}>
							{FormatDate(item.order_at, undefined, true)}
						</Link>
					);
				},
				renderExport: (item: ProductTransferApi) => FormatDate(item.order_at),
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: ProductTransferApi) => {
					return FormatDate(item.created_at, undefined, true);
				},
			},
			{
				id: "source_stock",
				name: t("modules.transfer.field.source_stock.title"),
				render: (item: ProductTransferApi) => (
					<RenderLimitedText
						minWidth={100}
						onClick={() => history.push(`/${organization.id}/settings/stocks/${item.stock.id}/edit`)}
						textStyleIsLink
					>
						{item.stock.name}
					</RenderLimitedText>
				),
				renderExport: (item: ProductTransferApi) => item.stock.name,
			},
			{
				id: "destination_stock",
				name: t("modules.transfer.field.destination_stock.title"),
				render: (item: ProductTransferApi) => (
					<RenderLimitedText
						minWidth={100}
						onClick={() =>
							history.push(`/${organization.id}/settings/stocks/${item.destination_stock.id}/edit`)
						}
						textStyleIsLink
					>
						{item.destination_stock.name}
					</RenderLimitedText>
				),
				renderExport: (item: ProductTransferApi) => item.destination_stock.name,
			},
			{
				id: "document_number",
				name: t("common.word.number", { ns: "lib" }),
				type: "text",
				render: (item: ProductTransferApi) => (
					<Link to={`/${organization.id}/documents/transfers/${item.order_id}`}>{item.document_number}</Link>
				),
				renderExport: (item: ProductTransferApi) => item.document_number,
			},
			{
				id: "reference_number",
				name: t("common.word.reference_number"),
				type: "text",
				render: (item: ProductTransferApi) => item.reference_number,
			},
			{
				id: "volume",
				name: t("common.word.volume"),
				render: (item: ProductTransferApi) => item.volume,
			},
			{
				id: "amount",
				name: t("common.word.quantity"),
				render: (item: ProductTransferApi) => item.amount,
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				render: (item: ProductTransferApi) => <AppFormatResourceStatusLabel status={item.status} />,
				renderExport: (item: ProductTransferApi) => t(`enums.common.status.${item.status}`),
			},
			{
				id: "comment",
				name: t("common.word.comment.title"),
				type: "text",
				render: (item: ProductTransferApi) => {
					return <RenderLimitedText minWidth={200}>{item.comment}</RenderLimitedText>;
				},
				renderExport: (item: ProductTransferApi) => item.comment,
			},
			{
				id: "total_fifo_cost_net_money",
				name: t("common.word.cost"),
				render: (item: ProductTransferApi) => FormatMoney(item.total_fifo_cost_net_money),
			},
		],
		selectedColumns: [
			"date",
			"source_stock",
			"destination_stock",
			"document_number",
			"reference_number",
			"volume",
			"amount",
			"status",
			"comment",
			"total_fifo_cost_net_money",
		],
		segments: [
			{
				id: "basic",
				name: t("common.word.product"),
				slug: "basic",
				filters: [
					{
						filterId: "productIds",
						filterType: "search_select",
						condition: "e",
						value: productId.toString(),
						valueInfo: {
							[productId.toString()]: data.filter_values[0]?.value?.name,
						},
					},
				],
			},
		],
		filters: [
			{
				id: "productIds",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (queryString: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getProductSearchSelect(queryString, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "stockIds",
				name: t("common.word.stock"),
				type: "search_select",
				source: {
					request: (queryString: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getStockSearchSelect(queryString, params, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "source_stock",
				name: t("modules.transfer.field.source_stock.title"),
				type: "search_select",
				source: {
					request: (queryString: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getStockSearchSelect(queryString, params, { cancelToken: options?.token }),
				},
			},
			{
				id: "destination_stock",
				name: t("modules.transfer.field.destination_stock.title"),
				type: "search_select",
				source: {
					request: (queryString: string, params: Record<string, any>, options?: Record<string, any>) =>
						api.organization().getStockSearchSelect(queryString, params, { cancelToken: options?.token }),
				},
			},
			{
				id: "statuses",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				listOptions: {
					OPEN: t("enums.common.status.OPEN"),
					DRAFT: t("enums.common.status.DRAFT"),
					RECEIVED: t("enums.common.status.RECEIVED"),
					VOID: t("enums.common.status.VOID"),
				},
				hasDefaultOptions: false,
			},
			{
				id: "item_comment",
				name: t("common.word.item_comment"),
				type: "text",
			},
		],
		exportConfig: {
			title: t("modules.transfer.field.export_config.title"),
			filename: t("modules.transfer.header.title"),
			organization: organization.name,
		},
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "basic"),
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "all";
			return apiOrganization.getProductTransfers(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: (segment: SegmentType) => {
			return segmentListService.save(listName, resourceType, segment);
		},
		numberOfStickyColumnsAtTheStart: 1,
		doesIdColumnRedirectToPreviewPage: true,
		isNumberOfStickyColumnsDynamic: false,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List = ({ resource, productId }: ListProps) => {
	const { t } = useTranslation();
	const history = useHistory();
	const segmentContextValue = React.useContext(SegmentContext);
	const organization = useSelector(selectOrganization);
	const mobileActions = useContext(ProductMobileActionsContext);

	if (!resource) return null;

	const data = resource.read();
	const config = buildConfig(t, data, segmentContextValue, history, organization, productId);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<ListData config={config} shouldFilterAvailableMultipleActions mobileActions={mobileActions} />
		</Suspense>
	);
};

const OrganizationProductTransfersPage = () => {
	const location = useLocation();
	const productId = location.pathname.split("/")[3];
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);

	useEffect(() => {
		setResource(
			wrapPromise(
				segmentContextValue.get(listName, resourceType, undefined, undefined, `productIds|e=${productId}`)
			)
		);
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} productId={productId} />
		</Suspense>
	);
};

export default OrganizationProductTransfersPage;
